/**
 * useTimelineDimensions Hook
 *
 * Calculates all timeline dimensions and positions including:
 * - Total timeline width
 * - NOW line position
 * - Frost date positions
 * - Crop row positions (with group gaps)
 * - Total rows height
 */

import { useMemo, useCallback } from 'react'
import {
  calculateGroupRowPositions,
  calculateTotalRowsHeight
} from '../utils/timeline-layout-utils'
import {
  COLLAPSED_ROW_HEIGHT,
  COLLAPSED_ROW_HEIGHT_GAP,
  PRINT_COLLAPSED_ROW_HEIGHT,
  PRINT_COLLAPSED_ROW_HEIGHT_GAP,
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  GROUP_GAP,
  GROUP_HEADER_HEIGHT,
  SUCCESSION_THIN_HEIGHT,
  SUCCESSION_THIN_GAP,
  SUCCESSION_EXPANDED_HEIGHT,
  WEEK_MS,
} from '../timeline-constants'

/**
 * useTimelineDimensions - Calculate timeline dimensions and positions
 *
 * All calculations are memoized for performance.
 *
 * @param {Object} params
 * @param {Object} params.timelineDateRange - { start, end } Date objects
 * @param {number} params.totalWeeks - Number of weeks in timeline
 * @param {number} params.weekWidth - Pixels per week (zoom level)
 * @param {Object} params.frostDateRange - { lfd, ffd } Date objects
 * @param {Array} params.groups - Grouped crops array
 * @param {Array} params.expandedIds - Expanded crop IDs
 * @param {boolean} params.forceAllExpand - Force all crops expanded
 * @param {Date} params.cutoffDate - Task filter cutoff date (hover preview)
 * @param {string} params.viewStrategy - 'phases' | 'tasks' | 'both' (doubles row height when 'both')
 *
 * @returns {Object} Dimension values and helper functions
 *   - TIMELINE_WIDTH: Total SVG width
 *   - NOW_X: Current date X position
 *   - CUTOFF_X: Task filter cutoff X position (hover preview)
 *   - FROST_DATES: Array of frost marker objects
 *   - cropPositions: Map of crop ID → position data
 *   - totalRowsHeight: Total height of all rows
 *   - getRowY: Function to get Y position for crop ID
 */
export const useTimelineDimensions = ({
  timelineDateRange,
  totalWeeks,
  weekWidth,
  frostDateRange,
  groups,
  expandedIds,
  forceAllExpand,
  cutoffDate = null,
  viewStrategy = 'phases',
  successionMap = null,
  showSuccessions = false,
  reports = [],
  printMode = false,
}) => {
  const collapsedRowHeight = printMode ? PRINT_COLLAPSED_ROW_HEIGHT : COLLAPSED_ROW_HEIGHT
  const collapsedRowHeightGap = printMode ? PRINT_COLLAPSED_ROW_HEIGHT_GAP : COLLAPSED_ROW_HEIGHT_GAP
  // Total timeline width
  const TIMELINE_WIDTH = useMemo(
    () => totalWeeks * weekWidth,
    [totalWeeks, weekWidth]
  )

  // NOW line X position
  const NOW_X = useMemo(
    () => ((new Date() - timelineDateRange.start) / (WEEK_MS)) * weekWidth,
    [timelineDateRange.start, weekWidth]
  )

  // Task filter cutoff line X position (hover preview)
  const CUTOFF_X = useMemo(() => {
    if (!cutoffDate) return null

    const cutoffTime = cutoffDate.getTime()
    const startTime = timelineDateRange.start.getTime()
    const msPerWeek = WEEK_MS

    const weeks = (cutoffTime - startTime) / msPerWeek
    return weeks * weekWidth
  }, [cutoffDate, timelineDateRange.start, weekWidth])

  // Report marker positions
  const REPORT_DATES = useMemo(() => {
    if (!reports.length || !timelineDateRange.start) return []
    return reports.map(r => ({
      x: ((new Date(r.created_at) - timelineDateRange.start) / (WEEK_MS)) * weekWidth,
      dateString: new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      label: (r.report_types?.name || ''),
      color: r.color, // populated by crop-timeline.jsx using TimelineStyles.report
      icon: r.icon, // populated by crop-timeline.jsx using TimelineStyles.report
    }))
  }, [reports, timelineDateRange.start, weekWidth])

  // Frost date marker positions
  const FROST_DATES = useMemo(() => {
    if (!frostDateRange || !timelineDateRange.start) return []

    return [
      { code: 'lfd', label: 'Last Frost' },
      { code: 'ffd', label: 'First Frost' },
    ].map(({ code, label }) => ({
      x: ((frostDateRange[code] - timelineDateRange.start) / (WEEK_MS)) * weekWidth,
      dateString: frostDateRange[code].toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      label: `${label} Estimate`,
      color: frostDateRange.color,
      icon: '❄️',
      dash: '6,2',
    }))
  }, [frostDateRange, timelineDateRange.start, weekWidth])

  // Build succession config for layout functions
  const successionConfig = useMemo(() => {
    if (!showSuccessions || !successionMap?.size) return null
    return {
      showSuccessions,
      successionMap,
      viewStrategy,
      SUCCESSION_THIN_HEIGHT,
      SUCCESSION_THIN_GAP,
      SUCCESSION_EXPANDED_HEIGHT,
    }
  }, [showSuccessions, successionMap, viewStrategy])

  // Crop position map (crop ID → { y, groupId, isGroupStart, isExpanded })
  const cropPositions = useMemo(() => {
    return calculateGroupRowPositions(
      groups,
      expandedIds,
      forceAllExpand,
      collapsedRowHeight,
      EXPANDED_ROW_HEIGHT,
      collapsedRowHeightGap,
      GROUP_GAP,
      GROUP_HEADER_HEIGHT,
      viewStrategy,
      successionConfig
    )
  }, [groups, expandedIds, forceAllExpand, viewStrategy, successionConfig, collapsedRowHeight, collapsedRowHeightGap])

  // Total height of all rows
  const totalRowsHeight = useMemo(() => {
    return calculateTotalRowsHeight(
      groups,
      expandedIds,
      forceAllExpand,
      collapsedRowHeight,
      EXPANDED_ROW_HEIGHT,
      collapsedRowHeightGap,
      GROUP_GAP,
      GROUP_HEADER_HEIGHT,
      viewStrategy,
      successionConfig
    )
  }, [groups, expandedIds, forceAllExpand, viewStrategy, successionConfig, collapsedRowHeight, collapsedRowHeightGap])

  // Y position getter (O(1) lookup via Map)
  const getRowY = useCallback((cropId) => {
    const position = cropPositions.get(cropId)
    return position ? position.y : 0
  }, [cropPositions])

  return {
    TIMELINE_WIDTH,
    NOW_X,
    CUTOFF_X,
    FROST_DATES,
    REPORT_DATES,
    cropPositions,
    totalRowsHeight,
    getRowY,
    collapsedRowHeight,
    collapsedRowHeightGap,
  }
}
